import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SearchableSelect from '../Common/SearchableSelect';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import siteVisitAssignmentService from '../../services/siteVisitAssignmentService';
import SiteVisitAssignmentRemarkModal from './SiteVisitAssignmentRemarkModal';

/**
 * Skip Visit Management UI — calls SiteVisitAssignment APIs only.
 */
const SiteVisitAssignmentPanel = ({ projectCode }) => {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [items, setItems] = useState([]);
  const [selectedNos, setSelectedNos] = useState([]);
  const [locked, setLocked] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);

  const loadSites = useCallback(async () => {
    if (!projectCode) {
      setSites([]);
      return;
    }
    setLoadingSites(true);
    setToast(null);
    try {
      const data = await siteVisitAssignmentService.getSites(projectCode);
      const list = (Array.isArray(data) ? data : [])
        .map((s) => {
          const id = s.siteID ?? s.siteId ?? s.SiteNo;
          const code = (s.siteCode ?? s.SiteCode ?? s.vSiteCode ?? '').toString().trim();
          const name = (s.siteName ?? s.SiteName ?? s.siteDescription ?? s.sitedescription ?? '')
            .toString()
            .trim();
          return {
            siteId: id != null ? String(id) : '',
            label: [code, name].filter(Boolean).join(' — ') || String(id ?? ''),
          };
        })
        .filter((s) => s.siteId)
        .sort((a, b) => a.label.localeCompare(b.label));
      setSites(list);
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.message || 'Failed to load sites.' });
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  }, [projectCode]);

  useEffect(() => {
    setSiteId('');
    setItems([]);
    setSelectedNos([]);
    setLocked(false);
    setToast(null);
    setShowRemarkModal(false);
    loadSites();
  }, [loadSites]);

  const loadSelection = useCallback(async (options = {}) => {
    if (!projectCode || !siteId) {
      setItems([]);
      setSelectedNos([]);
      setLocked(false);
      return;
    }
    setLoadingSelection(true);
    if (!options.preserveToast) setToast(null);
    try {
      const data = await siteVisitAssignmentService.getSelections(projectCode, siteId);
      const next = data?.items || [];
      const isLocked = !!data?.locked;
      setItems(next);
      setLocked(isLocked);
      setSelectedNos(next.filter((i) => i.isSelected).map((i) => i.studyVisitTypeNo));
      if (isLocked) {
        setToast({
          type: 'info',
          message: 'Selection is locked because a visit is already scheduled for this site.',
        });
      }
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.message || 'Failed to load visit assignment.' });
      setItems([]);
      setSelectedNos([]);
      setLocked(false);
    } finally {
      setLoadingSelection(false);
    }
  }, [projectCode, siteId]);

  useEffect(() => {
    loadSelection();
  }, [loadSelection]);

  const toggle = (studyVisitTypeNo) => {
    if (locked) return;
    setSelectedNos((prev) =>
      prev.includes(studyVisitTypeNo)
        ? prev.filter((n) => n !== studyVisitTypeNo)
        : [...prev, studyVisitTypeNo]
    );
  };

  const openRemarkModal = () => {
    if (!projectCode || !siteId || locked || saving) return;
    setShowRemarkModal(true);
  };

  const save = async (remark) => {
    if (!projectCode || !siteId || locked) return;
    const trimmedRemark = (remark || '').trim();
    if (!trimmedRemark) return;
    setSaving(true);
    setToast(null);
    try {
      await siteVisitAssignmentService.saveSelections({
        projectCode,
        siteId,
        studyVisitTypeNos: selectedNos,
        baseProfileCode: 'CRA',
        remark: trimmedRemark,
      });
      setShowRemarkModal(false);
      setToast({ type: 'success', message: 'Visit skipped successfully.' });
      await loadSelection({ preserveToast: true });
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.message || 'Failed to save visit assignment.' });
    } finally {
      setSaving(false);
    }
  };

  const siteOptions = useMemo(
    () => sites.map((s) => ({ value: s.siteId, label: s.label })),
    [sites]
  );

  if (loadingSites) return <Spinner />;

  return (
    <div className="sva-panel">
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}

      <div className="sva-form-row">
        <label htmlFor="sva-site">Site</label>
        <SearchableSelect
          name="siteId"
          id="sva-site"
          value={siteId}
          onChange={(e) => {
            setToast(null);
            setShowRemarkModal(false);
            setSiteId(e.target.value);
          }}
          options={siteOptions}
          placeholder="Select site"
          emptyOptionLabel="Select site"
          disabled={sites.length === 0}
          searchPlaceholder="Search by site code or name…"
        />
      </div>

      {!siteId ? (
        <p className="sva-empty">Select a site to assign visit types.</p>
      ) : loadingSelection ? (
        <Spinner />
      ) : items.length === 0 ? (
        <p className="sva-empty">
          No visit types assigned to this project. Assign them under Study Configuration → Project
          Visit Types.
        </p>
      ) : (
        <>
          <div className="sva-type-grid">
            {items.map((vt) => (
              <label key={vt.studyVisitTypeNo} className="sva-type-item">
                <input
                  type="checkbox"
                  checked={selectedNos.includes(vt.studyVisitTypeNo)}
                  onChange={() => toggle(vt.studyVisitTypeNo)}
                  disabled={locked}
                />
                <span>
                  <strong>{vt.visitCode}</strong> — {vt.visitName}
                  {vt.beforeOnboarding ? ' (before onboarding)' : ''}
                </span>
              </label>
            ))}
          </div>
          <div className="sva-toolbar">
            <button
              type="button"
              className="sva-btn sva-btn-primary"
              onClick={openRemarkModal}
              disabled={saving || locked}
            >
              {saving ? 'Saving…' : 'Save visit assignment'}
            </button>
          </div>
        </>
      )}
      <SiteVisitAssignmentRemarkModal
        show={showRemarkModal}
        saving={saving}
        onClose={() => {
          if (!saving) setShowRemarkModal(false);
        }}
        onSave={save}
      />
    </div>
  );
};

export default SiteVisitAssignmentPanel;
